// River Poultry Design System
// Based on https://www.riverpoultry.com/

export const colors = {
  // River Poultry Brand Colors - Enhanced for better brand alignment
  primary: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#2E7D32', // River Poultry Green - Primary brand color
    600: '#1a5d1a',
    700: '#14532d',
    800: '#0f3a0f',
    900: '#0a2a0a',
  },
  secondary: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#E53E3E', // River Poultry Red - Secondary brand color
    600: '#c53030',
    700: '#9c1a1a',
    800: '#7c1515',
    900: '#5c1010',
  },
  accent: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#FF8C00', // River Poultry Orange - Accent color
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  background: {
    primary: '#ffffff',
    secondary: '#f8fafc',
    tertiary: '#f1f5f9',
    hover: '#f0f9f0',
    accent: '#f0fdf4',
  },
  surface: {
    primary: '#ffffff',
    secondary: '#f8fafc',
    tertiary: '#f1f5f9',
    elevated: '#ffffff',
    interactive: '#f0f9f0',
  },
  border: {
    primary: '#e2e8f0',
    secondary: '#cbd5e1',
    light: '#f1f5f9',
    medium: '#e2e8f0',
    dark: '#94a3b8',
    accent: '#bbf7d0',
    brand: '#2E7D32',
  },
  text: {
    primary: '#1e293b',
    secondary: '#64748b',
    tertiary: '#94a3b8',
    muted: '#64748b',
    inverse: '#ffffff',
    brand: '#2E7D32',
    brandSecondary: '#E53E3E',
  },
  status: {
    success: '#22c55e',
    successLight: '#dcfce7',
    successDark: '#16a34a',
    error: '#ef4444',
    errorLight: '#fee2e2',
    errorDark: '#dc2626',
    warning: '#f59e0b',
    warningLight: '#fef3c7',
    warningDark: '#d97706',
    info: '#3b82f6',
    infoLight: '#dbeafe',
    infoDark: '#2563eb',
  },
  overlay: {
    dark: 'rgba(0, 0, 0, 0.5)',
    light: 'rgba(0, 0, 0, 0.1)',
    brand: 'rgba(46, 125, 50, 0.1)',
  }
};

export const typography = {
  fontFamily: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
  },
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
    '6xl': '3.75rem', // 60px
  },
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  lineHeight: {
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  }
};

export const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  7: '1.75rem',   // 28px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
  32: '8rem',     // 128px
};

export const borderRadius = {
  none: '0',
  sm: '0.125rem',   // 2px
  base: '0.25rem',  // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  '2xl': '1rem',    // 16px
  '3xl': '1.5rem',  // 24px
  full: '9999px',
};

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  none: 'none',
  card: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  cardHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  button: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  buttonHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

export const transitions = {
  fast: '150ms ease-in-out',
  normal: '250ms ease-in-out',
  slow: '350ms ease-in-out',
  default: '250ms ease-in-out',
};

export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
};

// River Poultry specific design tokens
export const riverPoultry = {
  // Brand-specific gradients - Enhanced for better visual appeal
  gradients: {
    primary: 'linear-gradient(135deg, #2E7D32 0%, #1a5d1a 100%)',
    primaryLight: 'linear-gradient(135deg, #4ade80 0%, #2E7D32 100%)',
    secondary: 'linear-gradient(135deg, #E53E3E 0%, #c53030 100%)',
    secondaryLight: 'linear-gradient(135deg, #f87171 0%, #E53E3E 100%)',
    accent: 'linear-gradient(135deg, #FF8C00 0%, #ea580c 100%)',
    success: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    subtle: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    card: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  },
  
  // River Poultry brand colors - Enhanced palette
  brandColors: {
    primary: '#2E7D32',
    primaryDark: '#1a5d1a',
    primaryLight: '#4ade80',
    primaryLighter: '#86efac',
    secondary: '#E53E3E',
    secondaryDark: '#c53030',
    secondaryLight: '#f87171',
    secondaryLighter: '#fca5a5',
    green: '#2E7D32',
    red: '#E53E3E',
    orange: '#FF8C00',
    darkGreen: '#1a5d1a',
    lightGreen: '#4ade80',
    lighterGreen: '#86efac',
    // Additional brand colors for better design language
    cream: '#fef7ed',
    warmGray: '#f5f5f4',
    coolGray: '#f8fafc',
  },
  
  // Component-specific styles
  components: {
    button: {
      primary: {
        background: 'linear-gradient(135deg, #2E7D32 0%, #1a3a0f 100%)',
        color: '#ffffff',
        border: 'none',
        borderRadius: borderRadius.lg,
        padding: `${spacing[3]} ${spacing[6]}`,
        fontWeight: typography.fontWeight.semibold,
        transition: transitions.fast,
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: shadows.lg,
        }
      },
      secondary: {
        background: 'linear-gradient(135deg, #E53E3E 0%, #c53030 100%)',
        color: '#ffffff',
        border: 'none',
        borderRadius: borderRadius.lg,
        padding: `${spacing[3]} ${spacing[6]}`,
        fontWeight: typography.fontWeight.semibold,
        transition: transitions.fast,
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: shadows.lg,
        }
      }
    },
    card: {
      background: colors.surface.primary,
      borderRadius: borderRadius.xl,
      boxShadow: shadows.md,
      border: `1px solid ${colors.border.light}`,
      padding: spacing[6],
    },
    input: {
      background: colors.surface.primary,
      border: `1px solid ${colors.border.medium}`,
      borderRadius: borderRadius.lg,
      padding: `${spacing[3]} ${spacing[4]}`,
      fontSize: typography.fontSize.base,
      transition: transitions.fast,
      '&:focus': {
        outline: 'none',
        borderColor: colors.primary[500],
        boxShadow: `0 0 0 3px ${colors.primary[100]}`,
      }
    }
  }
};

// Responsive breakpoints
export const breakpoints = {
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

export const mediaQueries = {
  minXs: `@media (min-width: ${breakpoints.xs})`,
  minSm: `@media (min-width: ${breakpoints.sm})`,
  minMd: `@media (min-width: ${breakpoints.md})`,
  minLg: `@media (min-width: ${breakpoints.lg})`,
  minXl: `@media (min-width: ${breakpoints.xl})`,
  min2Xl: `@media (min-width: ${breakpoints['2xl']})`,
  maxXs: `@media (max-width: ${breakpoints.xs})`,
  maxSm: `@media (max-width: ${breakpoints.sm})`,
  maxMd: `@media (max-width: ${breakpoints.md})`,
  maxLg: `@media (max-width: ${breakpoints.lg})`,
  maxXl: `@media (max-width: ${breakpoints.xl})`,
  max2Xl: `@media (max-width: ${breakpoints['2xl']})`,
};

// Mobile-specific tokens
export const mobileSpacing = {
  1: '0.125rem',  // 2px
  2: '0.25rem',   // 4px
  3: '0.5rem',    // 8px
  4: '0.75rem',   // 12px
  5: '1rem',      // 16px
  6: '1.25rem',   // 20px
  8: '1.5rem',    // 24px
  10: '2rem',     // 32px
  12: '2.5rem',   // 40px
  16: '3rem',     // 48px
};

export const mobileTypography = {
  fontSize: {
    xs: '0.625rem',   // 10px
    sm: '0.75rem',    // 12px
    base: '0.875rem', // 14px
    lg: '1rem',       // 16px
    xl: '1.125rem',   // 18px
    '2xl': '1.25rem', // 20px
    '3xl': '1.5rem',  // 24px
  }
};

// Export all design tokens
export const designTokens = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  riverPoultry,
  breakpoints,
  mediaQueries,
  mobileSpacing,
  mobileTypography,
};

export default designTokens;